// FriendProfile — `/friends/:userId`
//
// Composed friend profile page. Renders only fields the backend chose to
// expose (privacy + per-friend overrides applied server-side). Layout mirrors
// PlaceProfile.jsx: hero gallery → name + meta → action button row →
// "Staying at" card → favorite spots horizontal row → unfriend at bottom.
//
// NB: this intentionally inlines an embla-carousel-react gallery rather than
// importing PlaceProfile's helper — Phase 2 will extract that into a shared
// component. The pattern below mirrors PlaceProfile's hero exactly so the
// extraction is mechanical.
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MessageCircle, Car, MapPin, Instagram, UserMinus, Award, Footprints } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { TonightCard } from "../components/PlaceCard";
import MediaCarousel from "../components/MediaCarousel";
import RideAndQRButtons from "../components/RideAndQRButtons";

const FLAG = { en: "🇬🇧", es: "🇪🇸", fr: "🇫🇷", pt: "🇵🇹", de: "🇩🇪" };
const BADGE_KEY = {
  explorer: "friend_profile_badge_explorer",
  enthusiast: "friend_profile_badge_enthusiast",
  connoisseur: "friend_profile_badge_connoisseur",
};

export default function FriendProfile() {
  const { userId } = useParams();
  const nav = useNavigate();
  const { t, lang } = useI18n();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unfriendOpen, setUnfriendOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const apply = (data) => { if (!cancelled) setProfile(data); };
    const fail = (e) => { if (cancelled) return; toast.error(e.response?.data?.detail || t("friend_profile_not_found")); };
    const done = () => { if (!cancelled) setLoading(false); };
    api.get(`/friends/${encodeURIComponent(userId)}/profile`).then(({ data }) => apply(data)).catch(fail).finally(done);
    return () => { cancelled = true; };
  }, [userId, t]);

  const name = profile?.display_name || "—";
  const initials = (profile?.display_name || "?").slice(0, 2).toUpperCase();
  const photos = (() => {
    if (!profile) return [];
    if (profile.photos && profile.photos.length) return profile.photos;
    if (profile.profile_photo) return [{ full: profile.profile_photo, medium: profile.profile_photo, media_type: "image" }];
    return [];
  })();

  if (loading) {
    return (
      <main className="min-h-screen bg-background grid place-items-center text-foreground/55" data-testid="friend-profile-loading">
        {t("friend_profile_loading")}
      </main>
    );
  }
  if (!profile) {
    return (
      <main className="min-h-screen bg-background grid place-items-center px-6 text-center" data-testid="friend-profile-error">
        <div>
          <div className="font-display text-2xl mb-2 text-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>{t("friend_profile_not_found")}</div>
          <Link to="/friends" className="btn-outline mt-4 inline-flex">{t("friend_profile_back")}</Link>
        </div>
      </main>
    );
  }

  const doUnfriend = async () => {
    if (!profile.friendship_id) return;
    setBusy(true);
    try {
      await api.delete(`/friends/${profile.friendship_id}`);
      toast.success(t("friends_toast_unfriended"));
      nav("/friends");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const waLink = profile.whatsapp ? `https://wa.me/${(profile.whatsapp || "").replace(/\D/g, "")}` : null;
  const igLink = profile.instagram ? `https://instagram.com/${(profile.instagram || "").replace(/^@/, "")}` : null;
  const hotel = profile.hotel;
  const rideWa = waLink && hotel
    ? `${waLink}?text=${encodeURIComponent(`Hi, I would like a ride to ${hotel.name}${hotel.address ? `, ${hotel.address}` : ""}`)}`
    : null;
  const navLink = hotel && hotel.lat != null && hotel.lng != null
    ? `https://www.google.com/maps/dir/?api=1&destination=${hotel.lat},${hotel.lng}`
    : null;
  const spots = profile.favorite_spots || [];

  return (
    <main className="min-h-screen bg-background pb-24" data-testid="friend-profile-page">
      {/* Hero gallery */}
      <MediaCarousel photos={photos} initials={initials} testIdPrefix="friend-hero" />

      {/* Floating back button (over hero) */}
      <button
        type="button"
        onClick={() => nav(-1)}
        aria-label={t("friend_profile_back")}
        data-testid="friend-profile-back"
        className="fixed top-4 left-4 z-30 w-11 h-11 grid place-items-center rounded-full"
        style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(8px)", color: "#F5F1E8", minHeight: 48, minWidth: 48 }}
      >
        <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
      </button>

      {/* Name + meta */}
      <section className="px-5 pt-5">
        <h1
          data-testid="friend-profile-name"
          style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, color: "#F5F1E8", lineHeight: 1.15 }}
        >
          {name}
        </h1>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {profile.traveler_badge && (
            <span
              data-testid="friend-traveler-badge"
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full"
              style={{
                background: "rgba(232,184,75,0.12)",
                border: "1px solid rgba(232,184,75,0.45)",
                color: "#E8B84B",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 12,
              }}
            >
              <Award className="w-3.5 h-3.5" strokeWidth={1.5} />
              {t(BADGE_KEY[profile.traveler_badge])} · {profile.visit_count}+
            </span>
          )}
          {profile.language && (
            <span
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-foreground/65"
              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", fontSize: 12, fontFamily: "'DM Sans', sans-serif" }}
            >
              <span aria-hidden>{FLAG[profile.language] || "🏳️"}</span>
              <span className="uppercase tracking-wide">{profile.language}</span>
            </span>
          )}
        </div>
        {profile.member_since && (
          <div className="mt-2 text-foreground/55" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13 }}>
            {t("friend_profile_member_since")} {profile.member_since.slice(0, 10)}
          </div>
        )}
      </section>

      {/* Action buttons */}
      {(waLink || rideWa || navLink || igLink) && (
        <section className="px-5 mt-6 flex flex-wrap gap-2" data-testid="friend-action-row">
          {waLink && (
            <a
              href={waLink}
              target="_blank" rel="noopener noreferrer"
              data-testid="friend-action-whatsapp"
              className="btn-gold py-2.5 px-4 text-xs"
              style={{ minHeight: 48 }}
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> {t("friend_profile_action_whatsapp")}
            </a>
          )}
          {rideWa && (
            <a
              href={rideWa}
              target="_blank" rel="noopener noreferrer"
              data-testid="friend-action-ride"
              className="btn-outline py-2.5 px-4 text-xs"
              style={{ minHeight: 48 }}
            >
              <Car className="w-4 h-4" strokeWidth={1.5} /> {t("friend_profile_action_ride")}
            </a>
          )}
          {navLink && (
            <a
              href={navLink}
              target="_blank" rel="noopener noreferrer"
              data-testid="friend-action-navigate"
              className="btn-outline py-2.5 px-4 text-xs"
              style={{ minHeight: 48 }}
            >
              <MapPin className="w-4 h-4" strokeWidth={1.5} /> {t("friend_profile_action_navigate")}
            </a>
          )}
          {igLink && (
            <a
              href={igLink}
              target="_blank" rel="noopener noreferrer"
              data-testid="friend-action-instagram"
              className="btn-outline py-2.5 px-4 text-xs"
              style={{ minHeight: 48 }}
            >
              <Instagram className="w-4 h-4" strokeWidth={1.5} /> {t("friend_profile_action_instagram")}
            </a>
          )}
        </section>
      )}

      {/* Staying at */}
      {hotel && (
        <section className="px-5 mt-8" data-testid="friend-staying-at">
          <div className="eyebrow mb-3">{t("friend_profile_staying_at")}</div>
          <div className="lux-card p-4">
            <div className="flex items-start gap-3">
              <span className="w-9 h-9 rounded-full grid place-items-center text-gold border border-gold-soft" style={{ background: "#171717" }}>
                <MapPin className="w-4 h-4" strokeWidth={1.5} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-foreground" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 500 }}>
                  {hotel.name}
                </div>
                {hotel.address && (
                  <div className="text-foreground/55 mt-0.5" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13 }}>
                    {hotel.address}
                  </div>
                )}
                <WalkingDistanceChip hotel={hotel} lang={lang} />
              </div>
            </div>
            <div className="mt-4">
              <RideAndQRButtons
                destination={{
                  name: hotel.name,
                  address: hotel.address,
                  lat: hotel.lat,
                  lng: hotel.lng,
                }}
                testidPrefix="friend-hotel"
                rideLabelKey="ride_request_driver_to_hotel"
              />
            </div>
          </div>
        </section>
      )}

      {/* Favorite spots */}
      {spots.length > 0 && (
        <section className="mt-10" data-testid="friend-favorite-spots">
          <div className="px-5 flex items-end justify-between mb-3">
            <div className="eyebrow">
              {profile.spots_source === "saved"
                ? `${name}${t("friend_profile_saved_suffix")}`
                : `${name}${t("friend_profile_favorites_suffix")}`}
            </div>
            {profile.spots_has_more && (
              <Link
                to="/places"
                data-testid="friend-spots-see-all"
                className="text-gold hover:opacity-80 transition-opacity"
                style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase" }}
              >
                {t("friend_profile_see_all")}
              </Link>
            )}
          </div>
          <div className="px-5 flex gap-3 overflow-x-auto no-scrollbar pb-2">
            {spots.map((b) => (
              <div key={b.id} className="flex-[0_0_75%] max-w-[280px]">
                <TonightCard b={b} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Unfriend */}
      <section className="px-5 mt-12 pb-8 grid place-items-center">
        <button
          type="button"
          onClick={() => setUnfriendOpen(true)}
          data-testid="friend-unfriend-btn"
          disabled={busy}
          className="hover:opacity-80 transition-opacity disabled:opacity-40"
          style={{
            color: "#e08989",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 13,
            minHeight: 48,
            padding: "0 16px",
          }}
        >
          <UserMinus className="w-4 h-4 inline-block mr-1.5 align-text-bottom" strokeWidth={1.5} />
          {t("friend_profile_unfriend").replace("{name}", name)}
        </button>
      </section>

      {/* Unfriend confirm — opaque #171717, gold border top, Cancel-left / Red-right */}
      {unfriendOpen && (
        <>
          <div
            onClick={() => !busy && setUnfriendOpen(false)}
            className="fixed inset-0 z-[80]"
            style={{ background: "rgba(0,0,0,0.85)" }}
          />
          <div
            data-testid="friend-unfriend-confirm"
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[81] w-[92vw] max-w-md p-6"
            style={{ background: "#171717", border: "1px solid rgba(232,184,75,0.3)", borderRadius: 20 }}
          >
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: "#F5F1E8", lineHeight: 1.2 }}>
              {t("friend_profile_unfriend").replace("{name}", name)}
            </h2>
            <p className="mt-3 text-foreground/65" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, lineHeight: 1.5 }}>
              {t("friend_profile_unfriend_confirm").replace("{name}", name)}
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              <button
                onClick={() => setUnfriendOpen(false)}
                disabled={busy}
                data-testid="friend-unfriend-cancel"
                className="transition-colors hover:bg-[rgba(232,184,75,0.06)] disabled:opacity-40"
                style={{
                  minHeight: 48,
                  background: "transparent",
                  border: "1px solid rgba(232,184,75,0.5)",
                  color: "#E8B84B",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 500,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  borderRadius: 12,
                }}
              >
                {t("friends_decline")}
              </button>
              <button
                onClick={doUnfriend}
                disabled={busy}
                data-testid="friend-unfriend-confirm-btn"
                className="disabled:opacity-40 hover:opacity-90 transition-opacity"
                style={{
                  minHeight: 48,
                  background: "#8B1A1A",
                  border: "1px solid #8B1A1A",
                  color: "#F5F1E8",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 13,
                  fontWeight: 500,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  borderRadius: 12,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                }}
              >
                <UserMinus className="w-4 h-4" strokeWidth={1.5} />
                {t("friend_profile_unfriend").replace("{name}", name).replace(name, "").trim()}
              </button>
            </div>
          </div>
        </>
      )}
    </main>
  );
}

/* ---------------- Walking distance chip ---------------- */
function readCachedCoords() {
  try {
    const raw = localStorage.getItem("habano.gps.last");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const lat = parsed?.lat ?? parsed?.coords?.lat;
    const lng = parsed?.lng ?? parsed?.coords?.lng;
    if (typeof lat === "number" && typeof lng === "number") {
      return { lat, lng };
    }
  } catch { /* ignore */ }
  return null;
}

function WalkingDistanceChip({ hotel, lang }) {
  const coords = readCachedCoords();
  if (!coords || hotel?.lat == null || hotel?.lng == null) return null;
  // Haversine in metres
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(hotel.lat - coords.lat);
  const dLon = toRad(hotel.lng - coords.lng);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(coords.lat)) * Math.cos(toRad(hotel.lat)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const meters = R * c;
  if (meters > 5000) return null;
  const minutes = Math.max(1, Math.round(meters / 80)); // ~80 m/min walking
  const label = lang === "es" ? `${minutes} min a pie` : `${minutes} min walk`;
  return (
    <span
      data-testid="friend-walk-chip"
      className="inline-flex items-center gap-1 mt-2"
      style={{
        background: "rgba(232,184,75,0.12)",
        border: "1px solid rgba(232,184,75,0.45)",
        color: "#E8B84B",
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 11,
        padding: "3px 10px",
        borderRadius: 999,
      }}
    >
      <Footprints className="w-3 h-3" strokeWidth={1.5} />
      {label}
    </span>
  );
}

